// src/components/Layout.js
// Updated: Tuesday, August 4, 2026 at 8:25 PM EDT

import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { logOut } from '../services/auth';
import { db } from '../services/firebase';
import {
  doc, onSnapshot, updateDoc, collection, query, where,
} from 'firebase/firestore';
import InviteMemberModal from './InviteMemberModal';
import { hasFinanceAccess as userHasFinanceAccess } from '../utils/financePermissions';


import {
  AppBar, Toolbar, Typography, Button, Drawer, List,
  ListItem, ListItemIcon, ListItemText, Box, Divider,
  CssBaseline, ListItemButton, Alert, AlertTitle, Link, IconButton,
  Badge, ThemeProvider, createTheme
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import PeopleIcon from '@mui/icons-material/People';
import EventIcon from '@mui/icons-material/Event';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ChurchIcon from '@mui/icons-material/Church';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import GroupsIcon from '@mui/icons-material/Groups';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LanguageIcon from '@mui/icons-material/Language';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AnnouncementIcon from '@mui/icons-material/Announcement';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import RateReviewIcon from '@mui/icons-material/RateReview';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance'; // NEW ICON

const drawerWidth = 240;

const defaultTheme = createTheme();

function Layout() {
  const { currentUser, userProfile, churchProfile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [dynamicTheme, setDynamicTheme] = useState(defaultTheme);

  useEffect(() => {
    if (churchProfile) {
      const newTheme = createTheme({
        palette: {
          primary: { main: churchProfile.theme?.primaryColor || '#1976d2' },
          secondary: { main: churchProfile.theme?.secondaryColor || '#dc004e' },
        },
      });
      setDynamicTheme(newTheme);
    } else {
      setDynamicTheme(defaultTheme);
    }
  }, [churchProfile]);


  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  useEffect(() => {
    if (!currentUser?.uid) return;
    const notificationsRef = collection(db, 'users', currentUser.uid, 'userNotifications');
    const q = query(notificationsRef, where('isRead', '==', false));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setUnreadNotifications(snapshot.size);
    });
    return () => unsubscribe();
  }, [currentUser]);

  const hasChurch = !!userProfile?.churchId;

  useEffect(() => {
    if (userProfile && !hasChurch) {
      const allowedPaths = ['/join-church', '/profile', '/help', '/notifications'];
      if (!allowedPaths.includes(location.pathname)) {
        navigate('/join-church');
      }
    }
  }, [userProfile, hasChurch, location.pathname, navigate]);


  useEffect(() => {
    if (churchProfile?.name) {
      document.title = `${churchProfile.name} | Church App`;
    } else {
      document.title = 'Church App';
    }
  }, [churchProfile?.name]);

  useEffect(() => {
    let link = document.querySelector("link[rel~='icon']");
    if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.getElementsByTagName('head')[0].appendChild(link); }
    link.href = churchProfile?.logoUrl || '/favicon.ico';
  }, [churchProfile?.logoUrl]);

  const handleCancelRequest = async () => {
    if (!currentUser) return;
    if (window.confirm("Are you sure you want to cancel your request to join this church?")) {
        try {
            const userDocRef = doc(db, 'users', currentUser.uid);
            await updateDoc(userDocRef, { churchId: null });
            await logOut();
            alert("Your join request has been cancelled. Please log in again to select a different church.");
            navigate('/login');
        } catch (error) {
            console.error("Error cancelling request:", error);
            alert("Failed to cancel your request. Please try again.");
        }
    }
  };

  const isApprovedUser = userProfile?.status === 'active';
  const hasAdminAccess = isApprovedUser && hasChurch && (userProfile?.role === 'Church Administrator' || userProfile?.role === 'Super Administrator');
  const canInvite = isApprovedUser && hasChurch && (hasAdminAccess || userProfile?.role === 'Leadership');
  const canViewInquiries = hasAdminAccess || userProfile?.canViewInquiries === true;
  const canSendNotifications = hasAdminAccess || userProfile?.canSendNotifications === true;
  const canEditHomePage = hasAdminAccess || userProfile?.canEditHomePage === true;

  const hasFinanceAccess = userHasFinanceAccess(userProfile);

  const approvedMenuItems = [
    { text: 'Home', icon: <HomeIcon />, path: '/' },
    { text: 'Announcements', icon: <AnnouncementIcon />, path: '/announcements' },
    { text: 'Prayer Wall', icon: <PlaylistAddCheckIcon />, path: '/prayer-wall' },
    { text: 'Testimony Wall', icon: <RateReviewIcon />, path: '/testimonies' },
    { text: 'Small Groups', icon: <GroupsIcon />, path: '/groups' },
    { text: 'Ways to Give', icon: <AttachMoneyIcon />, path: '/ways-to-give' },
    { text: 'Member Directory', icon: <PeopleIcon />, path: '/directory' },
    { text: 'Event Sign-ups', icon: <EventAvailableIcon />, path: '/register-events' },
    { text: 'Event Calendar', icon: <EventIcon />, path: '/calendar' },
    { text: 'Volunteer Opportunities', icon: <VolunteerActivismIcon />, path: '/volunteer' },
    { text: 'Sermon Archives', icon: <VideoLibraryIcon />, path: '/sermons' },
    { text: 'Bible Study Archives', icon: <OndemandVideoIcon />, path: '/bible-studies' },
    { text: 'Contact Us', icon: <ContactSupportIcon />, path: '/contact' },
  ];

  // Conditionally add Financials to the menu
  if (hasFinanceAccess) {
    approvedMenuItems.splice(5, 0, { // Insert after "Ways to Give" (index 5)
      text: 'Financial Stewardship', 
      icon: <AccountBalanceIcon />, 
      path: '/financials' 
    });
  }

  const drawerContent = (
    <div>
      <Toolbar />
      <Box sx={{ overflow: 'auto' }}>
        {isApprovedUser && hasChurch && (
          <>
            <List>
              {approvedMenuItems.map((item) => (
                <ListItem key={item.text} disablePadding>
                  <ListItemButton component={RouterLink} to={item.path} onClick={() => setMobileOpen(false)}>
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.text} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
            <Divider />
          </>
        )}
        <List>
          <ListItem disablePadding>
            <ListItemButton component={RouterLink} to="/profile" onClick={() => setMobileOpen(false)}>
              <ListItemIcon><AccountCircleIcon /></ListItemIcon>
              <ListItemText primary="My Profile" />
            </ListItemButton>
          </ListItem>
          {!hasChurch && (
            <ListItem disablePadding>
                <ListItemButton component={RouterLink} to="/join-church" onClick={() => setMobileOpen(false)}>
                    <ListItemIcon><ChurchIcon /></ListItemIcon>
                    <ListItemText primary="Join a Church" />
                </ListItemButton>
            </ListItem>
          )}
           <ListItem disablePadding>
            <ListItemButton component={RouterLink} to="/help" onClick={() => setMobileOpen(false)}>
              <ListItemIcon><HelpOutlineIcon /></ListItemIcon>
              <ListItemText primary="Help" />
            </ListItemButton>
          </ListItem>
          {canInvite && (
             <ListItem disablePadding>
                <ListItemButton onClick={() => { setIsInviteModalOpen(true); setMobileOpen(false); }}>
                  <ListItemIcon><PersonAddIcon /></ListItemIcon>
                  <ListItemText primary="Invite Member" />
                </ListItemButton>
              </ListItem>
          )}
          {(hasAdminAccess || canViewInquiries || canSendNotifications || canEditHomePage) && (
            <ListItem disablePadding>
              <ListItemButton component={RouterLink} to="/admin" onClick={() => setMobileOpen(false)}>
                <ListItemIcon><AdminPanelSettingsIcon /></ListItemIcon>
                <ListItemText primary="Admin Dashboard" />
              </ListItemButton>
            </ListItem>
          )}
        </List>
      </Box>
    </div>
  );

  return (
    <ThemeProvider theme={dynamicTheme}>
      <Box sx={{ display: 'flex' }}>
        <CssBaseline />
        <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
          <Toolbar>
            <IconButton color="inherit" aria-label="open drawer" edge="start" onClick={handleDrawerToggle} sx={{ mr: 2, display: { sm: 'none' } }}><MenuIcon /></IconButton>
            {churchProfile?.logoUrl && ( <RouterLink to="/" style={{lineHeight: 0}}><Box sx={{ display: 'flex', alignItems: 'center', height: '40px' }}><img src={churchProfile.logoUrl} alt="Church Logo" style={{ height: '100%', objectFit: 'contain' }} /></Box></RouterLink> )}
            <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, ml: 2 }}>{churchProfile?.name || 'Church Connect'}</Typography>
            {currentUser && (
              <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
                <IconButton
                  size="large"
                  aria-label="show new notifications"
                  color="inherit"
                  onClick={() => navigate('/notifications')}
                >
                  <Badge badgeContent={unreadNotifications} color="error">
                    <NotificationsIcon />
                  </Badge>
                </IconButton>
                {userProfile?.firstName && userProfile?.lastName && ( <Typography variant="subtitle1" color="inherit" sx={{ mx: 2, display: {xs: 'none', sm: 'block'} }}>{userProfile.firstName} {userProfile.lastName}</Typography> )}
                <Button color="inherit" component={RouterLink} to="/profile">Profile</Button>
                <Button color="inherit" onClick={async () => { try { await logOut(); navigate('/login'); } catch (error) { alert('Failed to log out: ' + error.message); } }}>Logout</Button>
              </Box>
            )}
          </Toolbar>
        </AppBar>

        <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}>
          <Drawer variant="temporary" open={mobileOpen} onClose={handleDrawerToggle} ModalProps={{ keepMounted: true }} sx={{ display: { xs: 'block', sm: 'none' }, '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth }, }}>{drawerContent}</Drawer>
          <Drawer variant="permanent" sx={{ display: { xs: 'none', sm: 'block' }, '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth }, }} open>{drawerContent}</Drawer>
        </Box>

        <Box component="main" sx={{ flexGrow: 1, p: 3, width: { sm: `calc(100% - ${drawerWidth}px)` } }}>
          <Toolbar />
          {userProfile?.status === 'pending_approval' && hasChurch && (
              <Alert
                severity="info"
                sx={{ mb: 3 }}
                action={<Button color="inherit" size="small" onClick={handleCancelRequest}>Cancel Request</Button>}
              >
                  <AlertTitle>Request Pending</AlertTitle>
                  Your request to join **{churchProfile?.name}** is pending approval. You have limited access until approved.
                  <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                      {churchProfile?.contactInfo?.phone && (<Link href={`tel:${churchProfile.contactInfo.phone}`} sx={{ display: 'flex', alignItems: 'center', color: 'inherit', textDecoration: 'none' }}><PhoneIcon fontSize="small" sx={{ mr: 0.5 }} /> <Typography variant="body2">{churchProfile.contactInfo.phone}</Typography></Link>)}
                      {churchProfile?.contactInfo?.email && (<Link href={`mailto:${churchProfile.contactInfo.email}`} sx={{ display: 'flex', alignItems: 'center', color: 'inherit', textDecoration: 'none' }}><EmailIcon fontSize="small" sx={{ mr: 0.5 }} /> <Typography variant="body2">{churchProfile.contactInfo.email}</Typography></Link>)}
                      {churchProfile?.contactInfo?.website && (<Link href={churchProfile.contactInfo.website} target="_blank" rel="noopener noreferrer" sx={{ display: 'flex', alignItems: 'center', color: 'inherit', textDecoration: 'none' }}><LanguageIcon fontSize="small" sx={{ mr: 0.5 }} /> <Typography variant="body2">Website</Typography></Link>)}
                  </Box>
              </Alert>
          )}
          {userProfile && !hasChurch && (
              <Alert
                severity="warning"
                sx={{ mb: 3 }}
                action={
                  <Button color="inherit" size="small" component={RouterLink} to="/join-church">
                    Join a Church
                  </Button>
                }
              >
                  <AlertTitle>Welcome!</AlertTitle>
                  You are not yet a member of a church community. Please find and join a church to get started.
              </Alert>
          )}
          <Outlet />
        </Box>
        <InviteMemberModal open={isInviteModalOpen} onClose={() => setIsInviteModalOpen(false)} />
      </Box>
    </ThemeProvider>
  );
}

export default Layout;